import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useRef,
} from 'react';
import { unreachableCaseError } from 'types';
import usePrevious from 'react-use/esm/usePrevious';
import prop from 'ramda/es/prop';
import uniqBy from 'ramda/es/uniqBy';
import { getCountdownSeconds, getExpectedSentAtFromSubmittedAt } from 'utils/report';
import type {
  UpdateReportMutation,
  UpdateReportMutationVariables,
  SubmitReportMutation,
  SubmitReportMutationVariables,
  CancelReportMutation,
  CancelReportMutationVariables,
  CreateAddendumMutation,
  CreateAddendumMutationVariables,
  UpdateAddendumMutation,
  UpdateAddendumMutationVariables,
  DeleteAddendumMutation,
  DeleteAddendumMutationVariables,
  SubmitAddendumMutation,
  SubmitAddendumMutationVariables,
  CancelAddendumMutation,
  CancelAddendumMutationVariables,
  SubmitPrelimReportMutation,
  SubmitPrelimReportMutationVariables,
  CancelPrelimReportMutation,
  CancelPrelimReportMutationVariables,
  SubmitReportInvalidInput,
  SubmitReportProblem,
} from 'generated/graphql';
import {
  worklistItemToSlateContent,
  getDefaultSlateFieldChildren,
  generateAddendumSlateContentForAddendum,
  createAddendumFieldName,
  getAllPicklistsForTemplate,
  normalizeSlateContent,
  convertFieldsToUnified,
  convertUnifiedToFields,
} from './utils';
import { env } from 'config/env';
import { nanoid } from 'nanoid';
import { useDebouncedCallback } from 'use-debounce';
import { reporterSavingState, ReporterSaveState } from 'config/recoilState';
import { useSetRecoilState, useRecoilState, useRecoilValue } from 'recoil';
import { AUTO_SAVE_DEBOUNCE_MS, AUTO_SAVE_MAX_WAIT_MS } from './constants';
import { Element } from 'domains/reporter/RichTextEditor/core';
import {
  UPDATE_REPORT,
  CREATE_ADDENDUM,
  SUBMIT_ADDENDUM,
  UPDATE_ADDENDUM,
  DELETE_ADDENDUM,
  SUBMIT_REPORT,
  CANCEL_REPORT,
  CANCEL_ADDENDUM,
  GET_WORKLIST_ITEM,
  SUBMIT_PRELIM_REPORT,
  CANCEL_PRELIM_REPORT,
} from 'modules/Apollo/queries';
import { FetchResult, useMutation } from '@apollo/client';
import type { SlateContent } from '../types';
import {
  reportStatusState,
  ReportStatus,
  hasReportContentChangedState,
  emptyPlaceholderDialogState,
  draftReportWorklistSmid,
  unsignedReportDialogState,
  defaultUnsignedReportWarningDialogState,
  reporterIsBypassAndSign,
  reporterIsBypassAndSignPrelim,
  prelimReportWorklistSmid,
  discardReportState,
  headingErrorState,
  InsertedTemplateDataSource,
  templateInsertRequestState,
  IN_FLIGHT_STATUSES,
  IN_FLIGHT_PRELIM_STATUSES,
  PROVISIONAL_SUBMIT_STATUSES,
  ADDENDUM_STATUSES,
  rightDrawerCatalogRefreshSequenceState,
} from '../state';
import type { ReportStatuses } from '../state';
import { usePicklistStore } from '../../Template/usePicklist';
import { useCountdown } from './useCountdown';
import {
  worklistUpdateBc,
  WorklistRefetchReason,
} from 'modules/EventsManager/worklistUpdateChannel';
import type { RichTextEditorComponentProps } from '../../RichTextEditor';
import { useRecorder } from 'common/Recorder/useRecorder';
import analytics from 'modules/analytics';
import { useToasterDispatch, EnqueueToastFn } from 'common/ui/Toaster';
import { type ToastKey } from 'common/ui/Toaster/Toast';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { reporter } from 'modules/analytics/constants';
import {
  useCrossTabEventsListener,
  NameSpaces,
  sendCrossTabEvent,
  ShowCrossTabNotification,
  CrossWindowDataRefetchType,
} from 'modules/EventsManager';
import { getPicklistsNodesFromParentNode } from '../../RichTextEditor/plugins/picklist/utils';
import { getEmptyPlaceholderFields } from '../../RichTextEditor/plugins/placeholder/utils';
import { createParagraph } from '../../RichTextEditor/plugins/paragraph/utils';
import { useSyncHeadingErrorState } from '../../RichTextEditor/plugins/headingError/useSyncHeadingErrorState';
import { logger } from 'modules/logger';
import { useGamepadBindings } from 'utils/gamepad';
import { useWebHIDBindings } from 'domains/reporter/WebHID/useWebHIDBindings';
import { GamepadActionId } from 'generated/graphql';
import { useEditorBackup } from 'hooks/useEditorBackup';
import { getEmptyRequiredFields } from '../../RichTextEditor/utils/requiredFields';
import { useTypedCharacters } from 'hooks/useTypedCharacters';
import { RICH_TEXT_EDITOR } from 'hooks/useMostRecentInput';
import { useRequiredFieldIndicator } from 'hooks/useRequiredFieldIndicator';
import { PageTypes } from 'utils/pageTypes';
import { FF, useSplitFlag, useFeatureFlagEnabled } from 'modules/feature-flags';
import { useCurrentCaseReport } from 'hooks/useCurrentCaseReport';
import { useUnsignedReportWarningDialog } from '../UnsignedReportWarningDialog/useUnsignedReportWarningDialog';
import { useWorklistItemAnalytics } from 'hooks/useWorklistItemAnalytics';
import { useDiscardReport } from 'hooks/useDiscardReport';
import { useFocusMode, FocusModeToggleMethod } from 'hooks/useFocusMode';
import {
  HandleAutoloadNextCaseParams,
  useWorklistAutoLoad,
  useMaybeAutoloadNextCase,
} from 'hooks/useWorklistAutoLoad';
import { RESPONSE_EVENTS as EXTENSION_RESPONSE_EVENTS } from '../../../extension/constants';
import {
  addExtensionListener,
  dispatchExtensionEvent,
  focusEvent,
  removeExtensionListener,
} from '../../../extension/extensionEventCreators';
import type {
  ReportMacros,
  LoadedReportWorklistItem,
  ReportTemplate,
} from 'hooks/useCurrentCaseReport';
import type { Macros } from '../../Template/usePicklist';
import { useReportVersion } from 'hooks/useReportVersion';
import { useManagePatientJacket } from 'hooks/useManagePatientJacket';
import type { AutoloadTemplateState } from 'hooks/useAutoloadTemplate';
import { useReporterHeartbeat } from 'hooks/useReporterHeartbeat';
import { useRecorderVoiceCommandEventsListener } from '../hooks/useRecorderVoiceCommandEventsListener';
import { OnSubmitParams, SubmitType } from './types';
import {
  ReportExitAction,
  ReportExitActionInputMethod,
} from 'domains/reporter/RichTextEditor/analytics/performance';
import { useReportExitActionData } from 'domains/reporter/RichTextEditor/analytics/useReportExitActionData';
import { telemetryContextFor } from 'modules/Apollo/types';
import { useSlateContext } from '../SlateContext';
import { useTemplateInsertionState } from 'hooks/useTemplateInsertionState';
import { isWorkspaceManagerAvailable } from 'modules/workspace-manager';
import { WorkstationIDSubmissionDialog } from './Dialog/WorkstationIDSubmissionDialog';
import { useSubmissionWorkstationID, type SubmissionTarget } from './useSubmissionWorkstationID';
import { useIsLinkCasesMutating } from '../LinkCases/hooks/useIsLinkCasesMutating';
import {
  isLinkCasesBlockedExtensionShortcutDetail,
  isLinkCasesBlockedShortcutAction,
  isLinkCasesBlockedVoiceAction,
} from '../LinkCases/utils';
import { sanitizeAddendumContentForPersistence } from './persistenceSanitizers';
import { useEmptyRequiredFieldsDialog } from './useEmptyRequiredFieldsDialog';

export const newline = { type: 'paragraph', children: [{ text: '' }] } as const;
export const ADDENDUM_NAME_PLACEHOLDER = 'New Addendum';

type UpdateType = 'contentChange' | 'templateApply';

type fieldsUpdateProps = {
  contentToSubmit: SlateContent;
  activateTemplateIDToSubmit?: string;
  isExplicitDraftSave?: boolean;
  workstationIDToSubmit?: string | null;
  errorMode?: 'default' | 'submissionWorkstationPatch';
  onSuccess?: () => void;
  onError?: () => void;
};

type Addendum = {
  content: SlateContent;
  name: string;
  ownerFirstName?: Nullable<string>;
  ownerLastName?: Nullable<string>;
  submittedAt?: Nullable<Date>;
};

export type FieldsState = {
  reportID: string;
  reporterToolbarMountPoint: Nullable<HTMLElement>;
  worklistItem: LoadedReportWorklistItem;
  previousWorklistItemRef: {
    current: Nullable<LoadedReportWorklistItem>;
  };
  content: SlateContent;
  finalizedAddendums: Addendum[];
  addendum: Nullable<Addendum>;
  addendumSMID: Nullable<string>;
  addendumSubmittedDate: Nullable<Date>;
  editorKey: string;
  macros: ReportMacros;
  /**
   * When loading the worklist item, the backend returns the default template in the response.
   * `activateTemplateID` is the id for that template that is applied by default when the user
   * opens the report, and also serves as the template id for any subsequent template insertions.
   */
  activateTemplateID?: string;
  updateType: Nullable<UpdateType>;
  touched?: boolean;
};

export type FieldsCountdownContextProps = {
  countdownSeconds: number | null;
};

const initFieldsState = ({
  worklistItem,
  previousWorklistItemRef,
  reporterToolbarMountPoint,
}: Readonly<{
  worklistItem: LoadedReportWorklistItem;
  previousWorklistItemRef: {
    current: Nullable<LoadedReportWorklistItem>;
  };
  reporterToolbarMountPoint: Nullable<HTMLElement>;
}>) => ({
  reportID: '',
  addendum: undefined,
  addendumSMID: undefined,
  addendumSubmittedDate: undefined,
  editorKey: nanoid(),
  reporterToolbarMountPoint,
  worklistItem,
  previousWorklistItemRef,
  updateType: undefined,
  content: worklistItemToSlateContent(worklistItem),
  macros: uniqBy(prop('smid'), [
    ...(worklistItem?.report?.template?.macros ?? []),
    ...(worklistItem?.report?.macros ?? []),
  ]),

  activateTemplateID: worklistItem?.report?.template?.id,
  finalizedAddendums:
    worklistItem?.report?.addendums
      ?.filter(({ submittedAt, sentAt: sentToMirthAt }) => {
        const addendumSecondsFromServer = getCountdownSeconds(
          getExpectedSentAtFromSubmittedAt(submittedAt)
        );
        return (
          (addendumSecondsFromServer != null && addendumSecondsFromServer < 0) ||
          sentToMirthAt !== null
        );
      })
      .map((addendum: any, index: any) => ({
        name: createAddendumFieldName({
          lastName: addendum.owner?.lastName,
          addendumNumber: index + 1,
          createdDate: addendum.created,
        }),
        content: generateAddendumSlateContentForAddendum(addendum) ?? [],
        ownerFirstName: addendum.owner?.firstName,
        ownerLastName: addendum.owner?.lastName,
        submittedAt: addendum.submittedAt ?? addendum.created,
      })) ?? [],
});

type ActionType =
  | {
      type: 'initFieldsState';
      payload: {
        worklistItem: LoadedReportWorklistItem;
        previousWorklistItemRef: {
          current: Nullable<LoadedReportWorklistItem>;
        };
        reporterToolbarMountPoint: HTMLElement;
      };
    }
  | {
      type: 'setReporterToolbarMountPoint';
      payload: {
        reporterToolbarMountPoint: HTMLElement;
      };
    }
  | {
      type: 'updateReportContent';
      payload: {
        content: SlateContent;
      };
    }
  | {
      type: 'insertTemplate';
      payload: {
        macros: ReportMacros;
        activateTemplateID: string;
      };
    }
  | {
      type: 'addAddendum';
    }
  | {
      type: 'addAddendumSMID';
      payload: {
        addendumSMID: string;
      };
    }
  | {
      type: 'submitAddendum';
      payload: {
        addendumSubmittedDate: Nullable<Date>;
      };
    }
  | {
      type: 'updateAddendumContent';
      payload: {
        addendumContent: SlateContent;
      };
    }
  | {
      type: 'restoreAddendum';
      payload: {
        addendum: Addendum;
        addendumSMID: string;
        addendumSubmittedDate: Nullable<Date>;
      };
    }
  | {
      type: 'clearAddAddendum';
    }
  | {
      type: 'finalizeProvisionalAddendum';
      payload: {
        submittingUserFirstName: Nullable<string>;
        submittingUserLastName: Nullable<string>;
        addendumSubmittedDate?: Nullable<Date>;
      };
    };

const fieldsReducer = (state: FieldsState, action: ActionType): FieldsState => {
  switch (action.type) {
    case 'initFieldsState':
      return initFieldsState({
        ...action.payload,
      });
    case 'setReporterToolbarMountPoint':
      return { ...state, ...action.payload };
    case 'updateReportContent':
      return {
        ...state,
        ...action.payload,
        // After every `insertTemplate` action, we will also receive a `updateReportContent` action
        // We don't want to attribute this action to the user, so we reset the `updateType` to null
        // Any subsequent changes will be attributed to the user
        updateType: 'contentChange',
      };
    case 'insertTemplate':
      return {
        ...state,
        ...action.payload,
        updateType: 'templateApply',
      };
    case 'addAddendum':
      return {
        ...state,
        addendum: {
          name: ADDENDUM_NAME_PLACEHOLDER,
          content: getDefaultSlateFieldChildren(),
        },
      };
    case 'restoreAddendum':
      let addendumContent = action.payload.addendum?.content;
      // We expect addendum content. If it's empty, it's likely an internal test case.
      if (action.payload.addendum?.content.length !== 0) {
        addendumContent =
          action.payload.addendum?.content.__typename === 'AddendumSironaSlate'
            ? action.payload.addendum?.content.sironaSlate
            : action.payload.addendum?.content.textBlob
                .split('\n')
                .map((text: any) => createParagraph(text));
      }
      return {
        ...state,
        addendumSMID: action.payload.addendumSMID,
        addendumSubmittedDate: action.payload.addendumSubmittedDate,
        addendum: {
          name: action.payload.addendum?.name ?? ADDENDUM_NAME_PLACEHOLDER,
          content: addendumContent,
        },
      };
    case 'updateAddendumContent':
      return {
        ...state,
        addendum: {
          name: state.addendum?.name ?? ADDENDUM_NAME_PLACEHOLDER,
          content: action.payload.addendumContent,
        },
        updateType: 'contentChange',
      };
    case 'addAddendumSMID':
      return { ...state, ...action.payload };
    case 'submitAddendum':
      return { ...state, ...action.payload };
    case 'clearAddAddendum':
      return { ...state, addendumSMID: undefined, addendum: undefined };
    case 'finalizeProvisionalAddendum':
      return {
        ...state,
        finalizedAddendums: [
          ...state.finalizedAddendums,
          {
            name: createAddendumFieldName({
              addendumNumber: state.finalizedAddendums.length + 1,
              lastName: action.payload.submittingUserLastName,
              createdDate: state.addendumSubmittedDate ?? action.payload.addendumSubmittedDate,
            }),
            content: state.addendum?.content,
            ownerFirstName: action.payload.submittingUserFirstName,
            ownerLastName: action.payload.submittingUserLastName,
            submittedAt: state.addendumSubmittedDate ?? action.payload.addendumSubmittedDate,
          },
        ],
        addendumSMID: undefined,
        addendum: undefined,
      };
    default:
      unreachableCaseError((action as unknown as ActionType).type);
  }

  return state;
};

export type FieldsDispatch = {
  dispatch: (arg1: ActionType) => void;
  handleInsertTemplate: () => void;
  onReportChange: (content: SlateContent) => void;
  onAddendumChange: (content: SlateContent) => void;
  handleSubmit: (options?: OnSubmitParams) => Promise<void>;
  handleCancelSubmit: () => Promise<void>;
  handleAddendumAdd: () => Promise<void>;
  handleCancelAddAddendum: (options?: OnSubmitParams) => Promise<void>;
  handleSaveDraft: (options?: OnSubmitParams) => Promise<void>;
};

const FieldsStateContext = createContext<Nullable<FieldsState>>(undefined);
const FieldsCountdownContext = createContext<Nullable<FieldsCountdownContextProps>>(undefined);
const FieldsDispatchContext = createContext<Nullable<FieldsDispatch>>(undefined);

const getReportSMID = (worklistItem: LoadedReportWorklistItem) => {
  const { report } = worklistItem;
  if (report == null) {
    throw new Error('Cannot update a report without a smid.');
  }

  return report.smid;
};

const enqueueAutoSaveErrorToast = ({
  enqueueToast,
  toastKey,
}: {
  enqueueToast: EnqueueToastFn;
  toastKey: string;
}) => {
  enqueueToast('Auto saving failed, contact Sirona if same error persists.', {
    toastKey,
    autoHideDuration: null,
    yOffset: 45,
    icon: 'redWarning',
  });
};

const getCaseInfo = (worklistItem: LoadedReportWorklistItem) =>
  `${worklistItem?.patientName ?? 'Patient Name: N/A'}, ${worklistItem?.studyDescription ?? 'Study Description: N/A'} (${worklistItem?.accessionNumber ?? 'Accession Number: N/A'})`;

const defaultToastOptions = {
  severity: 'error',
  position: 'bottom-right',
} as const;

export type FieldsProviderProps = Readonly<{
  worklistItem: LoadedReportWorklistItem;
  template?: ReportTemplate;
  clearTemplate: () => void;
  isDisabled: boolean;
  children?: React.ReactNode;
  reporterToolbarMountPoint: Nullable<HTMLElement>;
  isClaimedByMe: boolean;
  onInit?: RichTextEditorComponentProps['onInit'];
  onDestroy?: RichTextEditorComponentProps['onDestroy'];
  autoloadTemplateState?: AutoloadTemplateState;
  insertTemplate: (arg1: ReportTemplate) => void;
}>;

export const FieldsProvider = ({
  children,
  worklistItem,
  template,
  isDisabled,
  reporterToolbarMountPoint,
  insertTemplate,
  isClaimedByMe,
  autoloadTemplateState,
}: FieldsProviderProps): React.ReactElement => {
  const previousWorklistItemRef = useRef<LoadedReportWorklistItem | null>(null);
  const [state, dispatch] = useReducer(
    fieldsReducer,
    {
      worklistItem,
      previousWorklistItemRef,
      reporterToolbarMountPoint,
    },
    initFieldsState
  );
  const { content, activateTemplateID, macros, addendumSMID, addendum, updateType } = state;

  const analyticsData = useWorklistItemAnalytics({ context: APOLLO_QUERY_CONTEXT });
  const previousContent = usePrevious(content);
  const previousAddendum = usePrevious(addendum);
  const [isReporterSubmissionEnabled] = useSplitFlag(
    FF.ENABLE_REPORTER_SUBMISSION,
    (v) => v === 'on'
  );
  const { picklists, setPicklists, setMacros, macros: insertedMacros } = usePicklistStore();
  const [savingState, setSavingState] = useRecoilState(reporterSavingState);
  const [hasReportContentChanged, setHasReportContentChanged] = useRecoilState(
    hasReportContentChangedState
  );
  const setDraftReportWorklistSmid = useSetRecoilState(draftReportWorklistSmid);
  const setPrelimReportWorklistSmid = useSetRecoilState(prelimReportWorklistSmid);
  const setRightDrawerCatalogRefreshSequence = useSetRecoilState(
    rightDrawerCatalogRefreshSequenceState
  );
  const [unsignedReportDialogOptions, setUnsignedReportWarningDialogState] =
    useRecoilState(unsignedReportDialogState);
  const [status, setStatus] = useRecoilState<ReportStatuses>(reportStatusState);
  const hasHeadingError = useRecoilValue<boolean>(headingErrorState);
  const isLinkCasesMutating = useIsLinkCasesMutating();
  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const { refetch: refetchCurrentCase, currentCaseId, isAddendumLocked } = useCurrentCaseReport();
  const { onDialogActionSuccess, onDialogActionError } = useUnsignedReportWarningDialog();
  const { handleDiscardReport, handleOpenDiscardReportModal, handleKeepDraft } = useDiscardReport({
    context: APOLLO_QUERY_CONTEXT,
  });
  const discardReportOptions = useRecoilValue(discardReportState);
  const templateInsertRequest = useRecoilValue(templateInsertRequestState);
  const { clearTemplateInsertRequestIfMatches } = useTemplateInsertionState();
  // Guards against re-processing the same insert request
  const handledTemplateInsertRequestIdRef = useRef<string | null>(null);
  const { toggleFocusMode, isFocusModeOn, isFocusModeSettingEnabled } = useFocusMode({
    context: APOLLO_QUERY_CONTEXT,
  });
  const isBypassAndSign = useRecoilValue(reporterIsBypassAndSign);
  const isBypassAndSignPrelim = useRecoilValue(reporterIsBypassAndSignPrelim);
  const { incrementReportVersion } = useReportVersion();
  const { managePatientJacket } = useManagePatientJacket({ context: APOLLO_QUERY_CONTEXT });
  const { sendHeartbeat } = useReporterHeartbeat({
    context: APOLLO_QUERY_CONTEXT,
    reportStatus: status,
  });
  const [isPrelimSubmissionEnabled] = useFeatureFlagEnabled(FF.REPORTER_PRELIM);
  const [isAutoImpressionsEnabled] = useFeatureFlagEnabled(FF.REPORTER_AUTO_IMPRESSIONS);
  const [isReportFacadeEnabled] = useFeatureFlagEnabled(FF.REPORT_FACADE);
  const [isClearPatientContextEnabled] = useFeatureFlagEnabled(FF.CLEAR_PATIENT_CONTEXT);
  const [isWorklistUnifiedFieldsEnabled] = useFeatureFlagEnabled(FF.WORKLIST_UNIFIED_FIELDS);
  const { workstationID, resolveSubmissionWorkstationID, workstationPromptDialogProps } =
    useSubmissionWorkstationID({
      addendumSMID,
      worklistItem,
    });

  const isExtensionAvailable = isWorkspaceManagerAvailable();
  const isFocusModeAllowed = useMemo(() => {
    return (
      isFocusModeSettingEnabled &&
      !isDisabled &&
      ([ReportStatus.Idle, ReportStatus.Init] as unknown as Partial<ReportStatuses>).includes(
        status
      )
    );
  }, [isFocusModeSettingEnabled, isDisabled, status]);

  const me = data?.me;
  const [updateReport] = useMutation<UpdateReportMutation, UpdateReportMutationVariables>(
    UPDATE_REPORT,
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );
  const [submitReport] = useMutation<SubmitReportMutation, SubmitReportMutationVariables>(
    SUBMIT_REPORT,
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );
  const [cancelReport] = useMutation<CancelReportMutation, CancelReportMutationVariables>(
    CANCEL_REPORT,
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );
  const [submitPrelimReport] = useMutation<
    SubmitPrelimReportMutation,
    SubmitPrelimReportMutationVariables
  >(SUBMIT_PRELIM_REPORT, {
    context: APOLLO_QUERY_CONTEXT,
  });
  const [cancelPrelimReport] = useMutation<
    CancelPrelimReportMutation,
    CancelPrelimReportMutationVariables
  >(CANCEL_PRELIM_REPORT, {
    context: APOLLO_QUERY_CONTEXT,
  });
  const [createAddendum] = useMutation<CreateAddendumMutation, CreateAddendumMutationVariables>(
    CREATE_ADDENDUM,
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );
  const [submitAddendum] = useMutation<SubmitAddendumMutation, SubmitAddendumMutationVariables>(
    SUBMIT_ADDENDUM,
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );
  const [updateAddendum] = useMutation<UpdateAddendumMutation, UpdateAddendumMutationVariables>(
    UPDATE_ADDENDUM,
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );
  const [deleteAddendum] = useMutation<DeleteAddendumMutation, DeleteAddendumMutationVariables>(
    DELETE_ADDENDUM,
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );
  const [cancelAddendum] = useMutation<CancelAddendumMutation, CancelAddendumMutationVariables>(
    CANCEL_ADDENDUM,
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );
  const { enqueueToast, enqueueOrUpdateToast, closeToast } = useToasterDispatch();
  const [latestToastKey, setLatestToastKey] = useState<Nullable<ToastKey>>(null);
  const { countdownSeconds, startCountdown, cancelCountdown, clearTimer } = useCountdown({
    worklistItemSmid: worklistItem.smid,
  });
  const { trackTypedCharacters } = useTypedCharacters({
    id: RICH_TEXT_EDITOR,
    context: APOLLO_QUERY_CONTEXT,
  });
  const { isRecording, stopRecording } = useRecorder();
  const { resetEditorWithContent } = useSlateContext();

  const { setEditorBackup, restoreEditorBackup } = useEditorBackup();
  const { setAllRequiredFieldsTouched } = useRequiredFieldIndicator();

  const {
    isWorklistAutoLoadEnabled,
    loadDiscardedSmidsFromSessionStorage,
    saveDiscardedSmidsInSessionStorage,
  } = useWorklistAutoLoad({
    context: APOLLO_QUERY_CONTEXT,
  });

  const maybeAutoloadNextCase = useMaybeAutoloadNextCase({
    previousToastKey: latestToastKey,
    onToastDisplayed: (key?: Nullable<ToastKey>) => {
      setLatestToastKey(key);
    },
  });

  const maybeAutoloadNextCaseCallback = useCallback(
    async (
      isEnabled: boolean,
      defaultToastMsg: string,
      autoloadParams: HandleAutoloadNextCaseParams
    ): Promise<void> => {
      const result = await maybeAutoloadNextCase(isEnabled, defaultToastMsg, autoloadParams);

      sendCrossTabEvent(NameSpaces.CROSS_TAB_NOTIFICATION, {
        type: 'showCrossTabNotification',
        payload: {
          toastKey: nanoid(),
          targetPageTypes: [PageTypes.WORKLIST],
          message: result.toastMsg,
          ...result.toastOptions,
        },
      } as ShowCrossTabNotification);
    },
    [maybeAutoloadNextCase]
  );

  useEffect(() => {
    setMacros((macroState: Macros): Macros => {
      const oldMacros = macroState ?? [];
      const newMacros = macros ?? [];
      return uniqBy(prop('smid'), [...oldMacros, ...newMacros]);
    });
  }, [setMacros, macros]);

  const processCountdownState = useCallback(
    (params: {
      type: SubmitType;
      remainingTime: number;
      isBypass: boolean;
      submittedAt?: Nullable<Date>;
      expectedSentAt: Nullable<Date>;
      sentAt: Nullable<Date>;
    }) => {
      const { type, remainingTime, isBypass, submittedAt, expectedSentAt, sentAt } = params;
      const isPrelim = type === SubmitType.Prelim;
      if (remainingTime > 0 && !isBypass && sentAt == null) {
        if (isWorklistUnifiedFieldsEnabled) {
          startCountdown({ expectedSentAt });
        } else {
          startCountdown({ submittedAt });
        }
        setStatus(isPrelim ? ReportStatus.ProvisionalPrelimSubmit : ReportStatus.ProvisionalSubmit);
      } else {
        setStatus(isPrelim ? ReportStatus.Idle : ReportStatus.Submitted);
      }
    },
    [isWorklistUnifiedFieldsEnabled, setStatus, startCountdown]
  );

  const restoreStatus = useCallback(() => {
    const { report } = worklistItem;

    if (countdownSeconds != null || report == null) return;

    const { addendums } = report;

    const latestAddendum = addendums.find((addendum) => addendum.sentAt == null);

    if (latestAddendum) {
      const { submittedAt, sentAt: sentToMirthAt, smid, content } = latestAddendum;
      const expectedAddendumSentAt = getExpectedSentAtFromSubmittedAt(submittedAt);
      const addendumSecondsFromServer = getCountdownSeconds(expectedAddendumSentAt);
      const addendumPayload = {
        addendumSMID: smid,
        addendum: {
          name: !isClaimedByMe
            ? createAddendumFieldName({
                lastName: latestAddendum.owner?.lastName,
                addendumNumber: addendums.length,
                createdDate: latestAddendum.updated,
                updatedDate: latestAddendum.updated,
                isAddendumLocked,
              })
            : ADDENDUM_NAME_PLACEHOLDER,
          content,
        },
        addendumSubmittedDate: submittedAt,
      };

      if (addendumSecondsFromServer == null) {
        setStatus(ReportStatus.AddendumAdd);
        dispatch({
          type: 'restoreAddendum',
          payload: addendumPayload,
        });
      } else if (addendumSecondsFromServer > 0 && !isBypassAndSign && sentToMirthAt == null) {
        if (isWorklistUnifiedFieldsEnabled) {
          startCountdown({ expectedSentAt: expectedAddendumSentAt });
        } else {
          startCountdown({ submittedAt });
        }
        setStatus(ReportStatus.ProvisionalAddendumSubmit);
        dispatch({
          type: 'restoreAddendum',
          payload: addendumPayload,
        });
      } else {
        setStatus(ReportStatus.Submitted);
      }

      return;
    }

    const { submittedAt, sentAt } = report;
    const prelimSubmittedAt =
      (report as typeof report & { prelimSubmittedAt?: Nullable<Date> }).prelimSubmittedAt ?? null;
    const expectedSubmitSentAt = getExpectedSentAtFromSubmittedAt(submittedAt);
    const expectedPrelimSentAt = getExpectedSentAtFromSubmittedAt(prelimSubmittedAt);
    const remainingSubmitTime = getCountdownSeconds(expectedSubmitSentAt);
    const remainingPrelimSubmitTime = getCountdownSeconds(expectedPrelimSentAt);

    if (remainingSubmitTime != null) {
      processCountdownState({
        type: SubmitType.Final,
        remainingTime: remainingSubmitTime,
        isBypass: isBypassAndSign,
        submittedAt,
        expectedSentAt: expectedSubmitSentAt,
        sentAt,
      });
      return;
    }

    if (remainingPrelimSubmitTime != null) {
      processCountdownState({
        type: SubmitType.Prelim,
        remainingTime: remainingPrelimSubmitTime,
        isBypass: isBypassAndSignPrelim,
        submittedAt: prelimSubmittedAt,
        expectedSentAt: expectedPrelimSentAt,
        sentAt,
      });
      return;
    }
    // This line allows us to easily mock a specified report status within storybook
    env.STORYBOOK_STORYSHOTS !== 'true' && setStatus(ReportStatus.Idle);
  }, [
    worklistItem,
    countdownSeconds,
    setStatus,
    isBypassAndSign,
    isBypassAndSignPrelim,
    isClaimedByMe,
    isAddendumLocked,
    isWorklistUnifiedFieldsEnabled,
    startCountdown,
    processCountdownState,
  ]);

  useEffect(() => {
    if (worklistItem?.smid !== previousWorklistItemRef?.current?.smid) {
      previousWorklistItemRef.current = worklistItem;
      setHasReportContentChanged(false);
      setDraftReportWorklistSmid(null);
      setUnsignedReportWarningDialogState(defaultUnsignedReportWarningDialogState);
      restoreStatus();
    }
  }, [
    worklistItem,
    setHasReportContentChanged,
    setDraftReportWorklistSmid,
    setUnsignedReportWarningDialogState,
    restoreStatus,
  ]);

  useEffect(() => {
    if (reporterToolbarMountPoint != null) {
      dispatch({
        type: 'setReporterToolbarMountPoint',
        payload: {
          reporterToolbarMountPoint,
        },
      });
    }
  }, [reporterToolbarMountPoint]);

  const sendFieldsUpdate = useCallback(
    async ({
      activateTemplateIDToSubmit,
      isExplicitDraftSave,
      workstationIDToSubmit,
      errorMode = 'default',
      onSuccess,
      onError,
      content,
      updatedVersion,
    }: {
      activateTemplateIDToSubmit?: string;
      isExplicitDraftSave?: boolean;
      workstationIDToSubmit?: string | null;
      errorMode?: 'default' | 'submissionWorkstationPatch';
      onSuccess?: () => void;
      onError?: () => void;
      content: SlateContent;
      updatedVersion: Nullable<number>;
    }) => {
      // Only send picklists found on the current content
      const contentPicklistNodeIds = getPicklistsNodesFromParentNode({
        children: content,
      }).map(({ picklistID }) => picklistID);
      const filteredPicklists = picklists
        .filter(({ id }) => contentPicklistNodeIds.includes(id))
        .map(({ __typename, ...picklist }) => ({
          ...picklist,
          options: picklist.options.map(({ __typename, ...option }) => ({
            ...option,
            default: option.default != null ? option.default : false,
          })),
        }));

      /**
       * Helper to check if content has any anchor metadata
       * Anchors are only added by Report Facade operations
       */
      const hasAnchors = (sections: SlateContent): boolean => {
        const check = (node: any): boolean => {
          if (node == null || typeof node !== 'object') return false;
          if (node.data?.anchor) return true;
          if (Array.isArray(node)) {
            return node.some(check);
          }
          if (Array.isArray(node.children)) {
            return node.children.some(check);
          }
          return false;
        };
        return check(sections);
      };

      /**
       * Removes anchor metadata from Slate content before persistence.
       * Anchors are internal to Report Facade and shouldn't be persisted to the backend.
       */
      const sanitizeForPersistence = (sections: SlateContent): SlateContent => {
        let removedAnchors = 0;
        const walk = (node: any): any => {
          if (node == null || typeof node !== 'object') return node;
          const cloned: any = Array.isArray(node) ? [] : { ...node };
          if (Array.isArray(node)) {
            for (const child of node) cloned.push(walk(child));
            return cloned;
          }
          if (cloned.data && typeof cloned.data === 'object') {
            if ('anchor' in cloned.data) {
              const { anchor, ...rest } = cloned.data as any;
              removedAnchors++;
              cloned.data = rest;
            }
            if (cloned.data && Object.keys(cloned.data).length === 0) {
              delete cloned.data;
            }
          }
          if (Array.isArray(cloned.children)) {
            cloned.children = cloned.children.map(walk);
          }
          return cloned;
        };
        const sanitized = walk(sections) as SlateContent;
        if (removedAnchors > 0) {
          logger.info('[Fields.Context] Sanitized sections: removed anchor metadata', {
            removedAnchors,
          });
        }
        return sanitized;
      };

      // Only sanitize if Report Facade is enabled AND content has anchors
      // This avoids performance overhead for existing functionality that doesn't use the facade
      const sanitizedSections =
        isReportFacadeEnabled && hasAnchors(content) ? sanitizeForPersistence(content) : content;

      const workstationIDForSubmission = workstationIDToSubmit ?? workstationID;
      let didSucceed: boolean = false;

      await updateReport({
        variables: {
          smid: getReportSMID(worklistItem),
          worklistItemSmid: worklistItem.smid,
          payload: {
            sections: sanitizedSections,
            templateId: activateTemplateIDToSubmit,
            picklists: filteredPicklists,
            workstationID: workstationIDForSubmission,
            version: updatedVersion,
            macroIds: insertedMacros.map((macro) => macro.smid),
          },
        },
        onCompleted: async () => {
          didSucceed = true;
          if (isExplicitDraftSave) {
            closeToast(`update-report-error-${getReportSMID(worklistItem)}`);
            closeToast(`save-draft-error-${getReportSMID(worklistItem)}`);

            const defaultToastMsg = `Draft report for ${getCaseInfo(worklistItem)} has been saved.`;

            await maybeAutoloadNextCaseCallback(isWorklistAutoLoadEnabled, defaultToastMsg, {
              reportExitAction: ReportExitAction.Draft,
              defaultToastMsg,
              isAddendum: false,
            });

            setDraftReportWorklistSmid(worklistItem.smid);
            setHasReportContentChanged(false);

            onSuccess && onSuccess();
          } else {
            closeToast(`update-report-error-${getReportSMID(worklistItem)}`);
            setEditorBackup(convertFieldsToUnified(content));
            setHasReportContentChanged(updateType === 'contentChange');
          }
          setSavingState(ReporterSaveState.Saved);
        },
        onError: (e) => {
          const doesNotHavePermission = e.message.includes(
            'You do not have permission to perform this action'
          );
          if (errorMode === 'submissionWorkstationPatch') {
            // Submission-time workstation patching provides its own retryable messaging.
          } else if (isExplicitDraftSave) {
            enqueueToast(
              `Draft report for ${getCaseInfo(worklistItem)} could not be saved due to a connection error.`,
              {
                toastKey: `save-draft-error-${getReportSMID(worklistItem)}`,
                autoHideDuration: null,
                yOffset: 45,
                icon: 'redWarning',
              }
            );
          } else if (doesNotHavePermission) {
            if (isRecording) {
              stopRecording();
            }
            refetchCurrentCase && refetchCurrentCase();
            enqueueToast(
              `You no longer have permission to edit this report. It may have been claimed by another user or a clinic administrator.`,
              {
                toastKey: `update-report-error-${getReportSMID(worklistItem)}`,
                autoHideDuration: null,
                yOffset: 45,
                icon: 'redWarning',
                isUnique: true,
              }
            );
          } else {
            enqueueAutoSaveErrorToast({
              enqueueToast,
              toastKey: `update-report-error-${getReportSMID(worklistItem)}`,
            });
          }

          if (e.networkError == null) {
            restoreEditorBackup(convertFieldsToUnified(content));
          }

          if (doesNotHavePermission) {
            logger.warn(
              '[Fields.Context] User does not have permission to update report',
              {
                reportSmid: getReportSMID(worklistItem),
                content,
                isExplicitDraftSave,
              },
              e
            );
          } else {
            logger.error(
              '[Fields.Context] Error occurred when updating report',
              {
                reportSmid: getReportSMID(worklistItem),
                content,
                isExplicitDraftSave,
              },
              e
            );
          }

          setSavingState(ReporterSaveState.Error);
          setHasReportContentChanged(updateType === 'contentChange');

          onError && onError();
        },
      });

      return didSucceed;
    },
    [
      picklists,
      isReportFacadeEnabled,
      updateReport,
      worklistItem,
      workstationID,
      insertedMacros,
      setSavingState,
      closeToast,
      maybeAutoloadNextCaseCallback,
      isWorklistAutoLoadEnabled,
      setDraftReportWorklistSmid,
      setHasReportContentChanged,
      setEditorBackup,
      updateType,
      enqueueToast,
      isRecording,
      refetchCurrentCase,
      stopRecording,
      restoreEditorBackup,
    ]
  );

  const fieldsUpdate = useCallback(
    async ({
      contentToSubmit,
      activateTemplateIDToSubmit,
      isExplicitDraftSave = false,
      workstationIDToSubmit,
      errorMode = 'default',
      onSuccess,
      onError,
    }: fieldsUpdateProps) => {
      let content = contentToSubmit;
      if (content == null) {
        return;
      }

      content = convertUnifiedToFields(content);

      // Do not save the draft until its valid
      const emptyFieldCount = content.filter(
        (field: Node) => Element.isElement(field) && field.children.length === 0
      ).length;
      if (emptyFieldCount > 0) {
        logger.error(
          '[Fields.Context] Draft report has invalid field content (empty field children). Not saving.',
          {
            reportSmid: getReportSMID(worklistItem),
            emptyFieldCount,
            fieldsContent: contentToSubmit,
            unifiedContent: content,
          }
        );

        setSavingState(ReporterSaveState.Error);

        return;
      }

      // We need to ensure that the next version is correct before sending the update
      const updatedVersion = await incrementReportVersion();

      logger.info('[Fields.Context] Updating report...');

      return sendFieldsUpdate({
        activateTemplateIDToSubmit,
        isExplicitDraftSave,
        workstationIDToSubmit,
        errorMode,
        onSuccess,
        onError,
        content,
        updatedVersion,
      });
    },
    [setSavingState, worklistItem, incrementReportVersion, sendFieldsUpdate]
  );

  const fieldsUpdateDebounced = useDebouncedCallback(
    async ({ contentToSubmit, activateTemplateIDToSubmit }: fieldsUpdateProps) =>
      // autosave happens here
      fieldsUpdate({
        contentToSubmit,
        activateTemplateIDToSubmit,
      }),
    AUTO_SAVE_DEBOUNCE_MS,
    {
      trailing: true,
      leading: true,
      maxWait: AUTO_SAVE_MAX_WAIT_MS,
    }
  );

  const onReportChange = useCallback(
    (newContent: SlateContent) => {
      dispatch({
        type: 'updateReportContent',
        payload: {
          content: newContent,
        },
      });
    },
    [dispatch]
  );

  const contentForHeadingErrorState = ADDENDUM_STATUSES.includes(
    status as (typeof ADDENDUM_STATUSES)[number]
  )
    ? (addendum?.content ?? [])
    : content;
  useSyncHeadingErrorState(contentForHeadingErrorState);

  const addendumUpdate = useCallback(
    async ({
      contentToSubmit,
      addendumSMID,
      isExplicitDraftSave = false,
      workstationIDToSubmit,
      errorMode = 'default',
      onSuccess,
      onError,
    }: {
      contentToSubmit: SlateContent;
      addendumSMID: string;
      isExplicitDraftSave?: boolean;
      workstationIDToSubmit?: string | null;
      errorMode?: 'default' | 'submissionWorkstationPatch';
      onSuccess?: () => void;
      onError?: () => void;
    }): Promise<boolean> => {
      if (contentToSubmit == null || addendumSMID == null || isClaimedByMe === false) return false;

      const workstationIDForSubmission = workstationIDToSubmit ?? workstationID;
      const sanitizedContentToSubmit = sanitizeAddendumContentForPersistence(contentToSubmit);
      let didSucceed: boolean = false;

      await updateAddendum({
        variables: {
          smid: addendumSMID,
          payload: { content: sanitizedContentToSubmit, workstationID: workstationIDForSubmission },
        },
        onCompleted: async () => {
          didSucceed = true;
          setSavingState(ReporterSaveState.Saved);

          if (isExplicitDraftSave) {
            closeToast(`save-draft-addendum-error-${getReportSMID(worklistItem)}`);
            const baseToastMsg = `Draft addendum for ${getCaseInfo(worklistItem)} has been saved.`;

            await maybeAutoloadNextCaseCallback(isWorklistAutoLoadEnabled, baseToastMsg, {
              reportExitAction: ReportExitAction.Draft,
              defaultToastMsg: baseToastMsg,
              isAddendum: true,
            });

            setDraftReportWorklistSmid(worklistItem.smid);
            setHasReportContentChanged(false);
            onSuccess && onSuccess();
          } else {
            closeToast(`update-report-error-${getReportSMID(worklistItem)}`);
            setEditorBackup(contentToSubmit);
          }
        },
        onError: (e) => {
          if (errorMode === 'submissionWorkstationPatch') {
            // Submission-time workstation patching provides its own retryable messaging.
          } else if (isExplicitDraftSave) {
            enqueueToast(
              `Draft addendum for ${getCaseInfo(worklistItem)} could not be saved due to connection error.`,
              {
                toastKey: `save-draft-addendum-error-${getReportSMID(worklistItem)}`,
                autoHideDuration: null,
                yOffset: 45,
                icon: 'redWarning',
              }
            );
          } else {
            enqueueAutoSaveErrorToast({
              enqueueToast,
              toastKey: `update-addendum-error-${getReportSMID(worklistItem)}`,
            });
          }

          if (e.networkError == null) {
            restoreEditorBackup(contentToSubmit);
          }

          logger.error(
            '[Fields.Context] Error occurred when updating addendum',
            {
              addendumSmid: addendumSMID,
              content: contentToSubmit,
            },
            e
          );

          setSavingState(ReporterSaveState.Error);

          onError && onError();
        },
      });

      return didSucceed;
    },
    [
      isClaimedByMe,
      updateAddendum,
      workstationID,
      setSavingState,
      closeToast,
      worklistItem,
      maybeAutoloadNextCaseCallback,
      isWorklistAutoLoadEnabled,
      setDraftReportWorklistSmid,
      setHasReportContentChanged,
      setEditorBackup,
      enqueueToast,
      restoreEditorBackup,
    ]
  );

  const addendumUpdateDebounced = useDebouncedCallback(addendumUpdate, AUTO_SAVE_DEBOUNCE_MS, {
    trailing: true,
    leading: false,
    maxWait: AUTO_SAVE_MAX_WAIT_MS,
  });

  useEffect(() => {
    // When the current user no longer holds the claim, an open draft addendum must fall back to
    // Submitted so submit actions they can no longer perform aren't offered (e.g. the case was
    // unclaimed or the addendum discarded from another surface/tab). The one exception is a reader
    // viewing another user's still-live draft: that stays visible read-only. We detect it by the
    // draft still being present in the report and owned by a different user. Any other case — the
    // user's own draft, or a draft already removed server-side by a discard — clears the local copy
    // regardless of render ordering between the unclaim and the server-side delete.
    const isAnotherUsersLiveDraft = worklistItem?.report?.addendums?.some(
      ({ sentAt, owner }) => sentAt == null && owner?.id != null && owner.id !== me?.id
    );
    if (
      !isClaimedByMe &&
      status === ReportStatus.AddendumAdd &&
      addendum != null &&
      !isAnotherUsersLiveDraft
    ) {
      dispatch({ type: 'clearAddAddendum' });
      setStatus(ReportStatus.Submitted);
      addendumUpdateDebounced.cancel();
      setSavingState(ReporterSaveState.Saved);
    }
  }, [
    isClaimedByMe,
    worklistItem,
    me,
    status,
    addendum,
    setStatus,
    addendumUpdateDebounced,
    setSavingState,
  ]);

  const onAddendumChange = useCallback(
    (newAddendumContent: SlateContent) => {
      sendHeartbeat();
      dispatch({
        type: 'updateAddendumContent',
        payload: { addendumContent: newAddendumContent },
      });
    },
    [sendHeartbeat]
  );

  const handleSaveDraft = useCallback(
    async ({
      onSuccess,
      onError,
    }: {
      onSuccess?: () => void;
      onError?: () => void;
    } = {}) => {
      if (savingState === ReporterSaveState.Saving) {
        return;
      }

      // WF-1809: Records the just-drafted SMID so autoload excludes it for the
      // rest of the tab session - same bucket as discard. Without this, drafted
      // cases stayed eligible and the FE's items.find() looped between the two
      // earliest-sorted cases.
      const recordDiscardedSmid = () => {
        const discardedSmids = loadDiscardedSmidsFromSessionStorage();
        discardedSmids.add(worklistItem.smid);
        saveDiscardedSmidsInSessionStorage(discardedSmids);
      };

      if (status !== ReportStatus.AddendumAdd) {
        setSavingState(ReporterSaveState.Saving);
        await fieldsUpdate({
          contentToSubmit: content,
          activateTemplateIDToSubmit: activateTemplateID,
          isExplicitDraftSave: true,
          onSuccess: () => {
            recordDiscardedSmid();
            onSuccess?.();
          },
          onError,
        });
      } else if (status === ReportStatus.AddendumAdd && addendumSMID != null) {
        setSavingState(ReporterSaveState.Saving);
        await addendumUpdate({
          contentToSubmit: addendum?.content,
          addendumSMID,
          isExplicitDraftSave: true,
          onSuccess: () => {
            recordDiscardedSmid();
            onSuccess?.();
          },
          onError,
        });
      } else {
        logger.info('[Fields.Context] Draft report did not save.', {
          status,
          addendumSMID,
          worklistItemSmid: worklistItem.smid,
          content: status === ReportStatus.AddendumAdd ? addendum?.content : content,
        });
      }

      if (isRecording) {
        await stopRecording();
      }
    },
    [
      savingState,
      status,
      addendumSMID,
      isRecording,
      setSavingState,
      fieldsUpdate,
      content,
      activateTemplateID,
      addendumUpdate,
      addendum?.content,
      worklistItem.smid,
      stopRecording,
      loadDiscardedSmidsFromSessionStorage,
      saveDiscardedSmidsInSessionStorage,
    ]
  );

  const handleDraftAction = useCallback(async () => {
    if (discardReportOptions.open) {
      await fieldsUpdateDebounced.flush();
      handleKeepDraft();
      return;
    }
    if (unsignedReportDialogOptions.open) {
      handleSaveDraft({
        onSuccess: onDialogActionSuccess,
        onError: onDialogActionError,
      });
      return;
    }
    handleSaveDraft();
  }, [
    discardReportOptions.open,
    unsignedReportDialogOptions.open,
    fieldsUpdateDebounced,
    handleKeepDraft,
    handleSaveDraft,
    onDialogActionSuccess,
    onDialogActionError,
  ]);

  const handleInsertTemplate = useCallback(() => {
    if (template == null) return;

    logger.info('[Fields.Context] handleInsertTemplate triggered with template: ', {
      templateId: template.id,
      templateName: template.name,
      worklistItemTemplateId: worklistItem?.report?.template?.id,
      worklistItemTemplateName: worklistItem?.report?.template?.name,
    });

    const templateMacros = template.macros ?? [];
    const worklistMacros = worklistItem.report?.macros ?? [];

    dispatch({
      type: 'insertTemplate',
      payload: {
        macros: uniqBy(prop('smid'), [...templateMacros, ...worklistMacros]),
        activateTemplateID: template.id,
      },
    });

    setPicklists(getAllPicklistsForTemplate(template));
    const headingContent = normalizeSlateContent(
      template.content ?? convertFieldsToUnified(template.sections)
    );
    const templateToInsert = {
      ...template,
      content: headingContent,
    } as const;

    insertTemplate(templateToInsert);
    setHasReportContentChanged(false);
  }, [
    template,
    worklistItem?.report?.template?.id,
    worklistItem?.report?.template?.name,
    worklistItem.report?.macros,
    setPicklists,
    insertTemplate,
    setHasReportContentChanged,
  ]);

  const [placeholderFieldDialogState, setPlaceholderFieldDialogState] = useRecoilState(
    emptyPlaceholderDialogState
  );
  const {
    isOpenForCurrentCase: isEmptyRequiredFieldsDialogOpenForCurrentCase,
    open: openEmptyRequiredFieldsDialog,
  } = useEmptyRequiredFieldsDialog(worklistItem.smid);

  const { measureReportExitActionStartWithMetadata, measureReportExitActionEndWithMetadata } =
    useReportExitActionData();

  const handleCancelSubmit = useCallback(async () => {
    if (status === ReportStatus.ProvisionalSubmit) {
      const reportSmid = getReportSMID(worklistItem);

      cancelCountdown();
      setStatus(ReportStatus.Cancelling);
      await cancelReport({
        variables: { smid: reportSmid },
      });
      setRightDrawerCatalogRefreshSequence((previous) => previous + 1);
      setStatus(ReportStatus.Idle);
      analytics.track(reporter.usr.submitReportCancel, {
        ...analyticsData,
        smid: reportSmid,
      });
    } else if (status === ReportStatus.ProvisionalPrelimSubmit) {
      const reportSmid = getReportSMID(worklistItem);

      cancelCountdown();
      setStatus(ReportStatus.CancellingPrelim);
      await cancelPrelimReport({
        variables: { smid: reportSmid },
      });
      setStatus(ReportStatus.Idle);
      analytics.track(reporter.usr.submitPrelimReportCancel, {
        ...analyticsData,
        smid: reportSmid,
      });
    } else if (status === ReportStatus.ProvisionalAddendumSubmit) {
      cancelCountdown();
      if (addendumSMID == null) return;

      setStatus(ReportStatus.AddendumCancelling);
      analytics.track(reporter.usr.submitReportCancel, {
        ...analyticsData,
        smid: addendumSMID,
      });

      await cancelAddendum({
        variables: { smid: addendumSMID },
        update: (proxy, { data }) => {
          if (data == null) return;

          proxy.writeQuery({
            query: GET_WORKLIST_ITEM,
            data: {
              worklistItem: {
                ...worklistItem,
                report: {
                  ...worklistItem.report,
                  addendums: worklistItem?.report?.addendums?.map((addendum: any) => {
                    if (addendum.smid === data.cancelAddendum.smid) {
                      return { ...addendum, submittedAt: null };
                    }

                    return addendum;
                  }),
                },
              },
            },
          });
        },
      });
      setRightDrawerCatalogRefreshSequence((previous) => previous + 1);
      setStatus(ReportStatus.AddendumAdd);
      return;
    }
  }, [
    status,
    worklistItem,
    cancelCountdown,
    setStatus,
    cancelReport,
    cancelPrelimReport,
    analyticsData,
    addendumSMID,
    cancelAddendum,
    setRightDrawerCatalogRefreshSequence,
  ]);

  const handleSubmit: (options?: OnSubmitParams) => Promise<void> = useCallback(
    async (options) => {
      const isPrelim = options?.type === SubmitType.Prelim;
      if (!isReporterSubmissionEnabled || (!isPrelimSubmissionEnabled && isPrelim)) {
        logger.warn(
          '[Fields.Context] Attempted to sign but the user does not have the feature enabled',
          {
            reportSmid: getReportSMID(worklistItem),
            status,
          }
        );
        return;
      }

      if (options?.force !== true && savingState === ReporterSaveState.Saving) {
        logger.warn('[Fields.Context] Attempted to sign report without saving latest changes', {
          reportSmid: getReportSMID(worklistItem),
        });
        enqueueToast('Please wait until your changes are saved before signing the report.', {
          icon: 'yellowWarning',
        });
        return;
      }

      if (hasHeadingError === true) {
        logger.warn('[Fields.Context] Attempted to sign report with duplicate section headers', {
          reportSmid: getReportSMID(worklistItem),
          submitType: isPrelim ? SubmitType.Prelim : SubmitType.Final,
        });
        options?.onError && options?.onError();
        return;
      }

      if (
        options?.force !== true &&
        (placeholderFieldDialogState.open === true ||
          isEmptyRequiredFieldsDialogOpenForCurrentCase ||
          (
            [
              ReportStatus.Submitting,
              ReportStatus.ProvisionalSubmit,
              ReportStatus.SubmittingPrelim,
              ReportStatus.ProvisionalPrelimSubmit,
              ReportStatus.AddendumSubmitting,
              ReportStatus.ProvisionalAddendumSubmit,
            ] as unknown as ReportStatuses
          ).includes(status))
      ) {
        logger.warn('[Fields.Context] Attempted to sign report in an invalid state', {
          reportSmid: getReportSMID(worklistItem),
          status,
          placeholderFieldDialogOpen: placeholderFieldDialogState.open,
          emptyRequiredFieldsDialogStateOpen: isEmptyRequiredFieldsDialogOpenForCurrentCase,
        });

        return;
      }

      logger.debug('[FieldsProvider] Initiating report submission process...');

      await fieldsUpdateDebounced.flush();
      await addendumUpdateDebounced.flush();

      const reportSmid = getReportSMID(worklistItem);

      const warnIfEmptyRequiredFields = (slateContent: SlateContent | null): boolean => {
        // If it's a prelim report submission, we don't validate empty required fields
        if (!isPrelim && !isEmptyRequiredFieldsDialogOpenForCurrentCase && slateContent) {
          const emptyRequiredFields = getEmptyRequiredFields(slateContent);

          if (emptyRequiredFields.length > 0) {
            openEmptyRequiredFieldsDialog(emptyRequiredFields);

            setAllRequiredFieldsTouched();
            logger.info('[Fields.Context] Attempted to submit report with empty required fields', {
              count: emptyRequiredFields.length,
            });
            return true;
          }
        }
        return false;
      };

      const cancelSubmissionForMissingWorkstationID = (target: SubmissionTarget) => {
        const submissionLabel = target === 'addendum' ? 'Addendum' : 'Report';

        enqueueToast(`${submissionLabel} submission was cancelled.`, {
          icon: 'yellowWarning',
        });
        options?.onError && options?.onError();
      };

      const notifyWorkstationPatchFailure = (target: SubmissionTarget) => {
        enqueueToast(
          `Could not update the workstation ID before submitting this ${target}. Please try again.`,
          {
            icon: 'redWarning',
          }
        );
        options?.onError && options?.onError();
      };

      switch (status) {
        // No-op, this is the default state before we've interpreted the countdown state
        case ReportStatus.Init:
          return;
        case ReportStatus.Idle: {
          // When the Submit button is pressed, stop the dictation
          if (isRecording === true) {
            await stopRecording();
          }

          if (placeholderFieldDialogState.open === false) {
            const emptyPlaceholders = getEmptyPlaceholderFields(content);

            if (isPrelim === false && emptyPlaceholders.length > 0) {
              setPlaceholderFieldDialogState({
                emptyPlaceholders,
                open: true,
              });
              options?.onError && options?.onError();
              return;
            }
          }

          if (warnIfEmptyRequiredFields(content)) {
            options?.onError && options?.onError();
            return;
          }

          const reportWorkstationSubmission = !isPrelim
            ? await resolveSubmissionWorkstationID('report')
            : {
                cancelled: false,
                workstationIDToSubmit: workstationID,
                requiresPatchBeforeSubmit: false,
              };

          if (reportWorkstationSubmission.cancelled) {
            cancelSubmissionForMissingWorkstationID('report');
            return;
          }

          /* If there is no saved report and the editor content has not changed,
             the user is signing a report that is simply an unchanged template.
             In this case, we need to save the report, since no autosave will have yet happened. */
          const report = worklistItem.report;
          const created = report?.created;
          const sectionsLastUpdated = report?.sectionsLastUpdated;
          const hasSavedReportContent =
            created != null && sectionsLastUpdated != null && sectionsLastUpdated !== created;
          const shouldSaveReportBeforeSubmit = !hasReportContentChanged && !hasSavedReportContent;
          const needsPreSubmitReportUpdate =
            shouldSaveReportBeforeSubmit || reportWorkstationSubmission.requiresPatchBeforeSubmit;

          if (needsPreSubmitReportUpdate) {
            if (shouldSaveReportBeforeSubmit) {
              logger.info('[Fields.Context] Saving an unchanged report before signing ...', {
                reportSmid: getReportSMID(worklistItem),
              });
            }

            setSavingState(ReporterSaveState.Saving);
            const didUpdateReport = await fieldsUpdate({
              contentToSubmit: content,
              activateTemplateIDToSubmit: activateTemplateID,
              workstationIDToSubmit: reportWorkstationSubmission.workstationIDToSubmit,
              errorMode: reportWorkstationSubmission.requiresPatchBeforeSubmit
                ? 'submissionWorkstationPatch'
                : 'default',
            });

            if (reportWorkstationSubmission.requiresPatchBeforeSubmit && didUpdateReport !== true) {
              notifyWorkstationPatchFailure('report');
              return;
            }
          }

          // Now, submit the report.
          let statusToSet: ReportStatuses = ReportStatus.Submitting;
          let isBypass = isBypassAndSign;
          let submitType = SubmitType.Final;

          if (isPrelim) {
            statusToSet = ReportStatus.SubmittingPrelim;
            isBypass = isBypassAndSignPrelim;
            submitType = SubmitType.Prelim;
          }

          setStatus(statusToSet);

          let submitResult:
            | FetchResult<SubmitPrelimReportMutation>
            | FetchResult<SubmitReportMutation>
            | undefined;
          let errors: any;

          try {
            logger.info(
              `[Fields.Context] Attempting to submit ${submitType} report with bypass ${isBypass === true ? 'is' : 'is not'} enabled`
            );
            const variables = {
              smid: reportSmid,
              immediate_submit: isBypass,
            };
            submitResult = isPrelim
              ? await submitPrelimReport({ variables })
              : await submitReport({ variables });
            errors = submitResult?.errors;
          } catch (err: any) {
            errors = [err];
            logger.error(
              `[Fields.Context] Error occurred when submitting ${submitType} report`,
              {
                smid: reportSmid,
              },
              err
            );
          }

          analytics.track(isPrelim ? reporter.usr.submitPrelimReport : reporter.usr.submitReport, {
            ...analyticsData,
            smid: reportSmid,
          });

          if (!submitResult?.data || errors?.length) {
            setStatus(ReportStatus.Idle);
            const toastKey = enqueueOrUpdateToast(
              `Report ${reportSmid} was not signed, an error occurred.`,
              latestToastKey,
              defaultToastOptions
            );
            setLatestToastKey(toastKey);
            options?.onError && options?.onError();
            return;
          }

          // Handle submit problems
          const submitResultData: SubmitPrelimReportMutation | SubmitReportMutation | undefined =
            submitResult.data;
          let invalidInputs: SubmitReportInvalidInput[] = [];
          let message = '';

          const isSubmitReportProblem =
            (submitResultData as SubmitReportMutation)?.submitReport?.__typename ===
            'SubmitReportProblem';
          const isSubmitPrelimProblem =
            (submitResultData as SubmitPrelimReportMutation)?.submitPrelimReport?.__typename ===
            'SubmitPrelimReportProblem';

          if (isSubmitPrelimProblem) {
            message = `Preliminary report ${reportSmid} was not signed, an error occurred.`;
            const toastKey = enqueueOrUpdateToast(message, latestToastKey, defaultToastOptions);
            setLatestToastKey(toastKey);
          } else if (isSubmitReportProblem) {
            message = `Report ${reportSmid} was not signed, an error occurred.`;
            invalidInputs = [
              ...((
                (submitResultData as SubmitReportMutation)
                  .submitReport as unknown as SubmitReportProblem
              )?.invalidInputs?.filter(
                (input): input is SubmitReportInvalidInput => input != null
              ) ?? []),
            ];
            // Validating fields
            if (invalidInputs.length) {
              invalidInputs.forEach((input) => {
                if (input != null) {
                  let inputMessage: string = input.message;
                  if (inputMessage.includes('Impression section is empty.')) {
                    inputMessage += " Please make sure 'Impression:' is formatted as an H1 header.";
                  }
                  const toastKey = enqueueOrUpdateToast(
                    inputMessage,
                    latestToastKey,
                    defaultToastOptions
                  );
                  logger.warn(
                    '[Fields.Context] - "SubmitReportProblem" upon attempted submission - ',
                    inputMessage,
                    { input }
                  );
                  setLatestToastKey(toastKey);
                }
              });
            } else {
              const toastKey = enqueueOrUpdateToast(message, latestToastKey, defaultToastOptions);
              setLatestToastKey(toastKey);
            }
          }
          if (isSubmitPrelimProblem || isSubmitReportProblem) {
            setStatus(ReportStatus.Idle);
            return;
          }

          // If we reach this point, the report was successfully submitted
          trackTypedCharacters();

          setHasReportContentChanged(false);

          if (!isPrelim && isBypassAndSign) {
            setStatus(ReportStatus.Submitted);
          } else if (isPrelim && isBypassAndSignPrelim) {
            setStatus(ReportStatus.Idle);
            setPrelimReportWorklistSmid(worklistItem.smid);
          } else {
            startCountdown();
            setStatus(
              isPrelim ? ReportStatus.ProvisionalPrelimSubmit : ReportStatus.ProvisionalSubmit
            );
          }

          let exitAction: ReportExitAction;
          if (isBypass && isPrelim) {
            exitAction = ReportExitAction.BypassPrelim;
          } else if (isBypass) {
            exitAction = ReportExitAction.BypassSign;
          } else if (isPrelim) {
            exitAction = ReportExitAction.Prelim;
          } else {
            exitAction = ReportExitAction.Sign;
          }

          measureReportExitActionEndWithMetadata({
            exitAction,
          });

          const defaultToastMsg = isPrelim
            ? `Report for ${getCaseInfo(worklistItem)} has been marked as preliminary.`
            : `Report for ${getCaseInfo(worklistItem)} has been signed.`;

          await maybeAutoloadNextCaseCallback(isWorklistAutoLoadEnabled, defaultToastMsg, {
            reportExitAction: exitAction,
            defaultToastMsg,
            isAddendum: false,
            onAutoloadComplete: (nextCaseSmid) => {
              if (nextCaseSmid != null) {
                setPrelimReportWorklistSmid(null);
              }
            },
          });
          if (!isPrelim) {
            setRightDrawerCatalogRefreshSequence((previous) => previous + 1);
          }

          // If the user was closing the window, we want to close it now that the report has been signed
          options?.onSuccess && options.onSuccess();
          return;
        }
        // No-op, this is when the user is submitting a request to the server
        case ReportStatus.Submitting:
        case ReportStatus.SubmittingPrelim:
          return;
        case ReportStatus.ProvisionalSubmit:
        case ReportStatus.ProvisionalPrelimSubmit:
          return;
        // No-op, state cannot be changed while 'cancelling'
        case ReportStatus.Cancelling:
        case ReportStatus.CancellingPrelim:
          return;
        // No-op, button is disabled. Users add an addendum with a different button
        case ReportStatus.Submitted:
          return;
        case ReportStatus.AddendumAdd:
          if (isRecording === true) {
            await stopRecording();
          }

          await addendumUpdateDebounced.flush();

          if (warnIfEmptyRequiredFields(addendum?.content)) {
            options?.onError && options?.onError();
            return;
          }

          if (addendumSMID == null) {
            options?.onError && options?.onError();
            return;
          }

          const addendumWorkstationSubmission = await resolveSubmissionWorkstationID('addendum');

          if (addendumWorkstationSubmission.cancelled) {
            cancelSubmissionForMissingWorkstationID('addendum');
            return;
          }

          if (addendumWorkstationSubmission.requiresPatchBeforeSubmit) {
            setSavingState(ReporterSaveState.Saving);
            const didPatchWorkstationID = await addendumUpdate({
              contentToSubmit: addendum?.content,
              addendumSMID,
              workstationIDToSubmit: addendumWorkstationSubmission.workstationIDToSubmit,
              errorMode: 'submissionWorkstationPatch',
            });

            if (didPatchWorkstationID !== true) {
              notifyWorkstationPatchFailure('addendum');
              return;
            }
          }

          setStatus(ReportStatus.AddendumSubmitting);

          analytics.track(reporter.usr.submitAddendum, {
            ...analyticsData,
            smid: addendumSMID,
          });
          trackTypedCharacters();

          const { data: submitAddendumData } = await submitAddendum({
            variables: { smid: addendumSMID, immediate_submit: isBypassAndSign },
            update: (proxy, { data }) => {
              if (data == null) return;
              const addendums = worklistItem?.report?.addendums || [];
              proxy.writeQuery({
                query: GET_WORKLIST_ITEM,
                data: {
                  worklistItem: {
                    ...worklistItem,
                    report: {
                      ...worklistItem.report,
                      addendums: addendums.map((a) => {
                        if (a.smid === data.submitAddendum.smid) return data.submitAddendum;
                        return a;
                      }),
                    },
                  },
                },
              });
            },
            onError: (e) => {
              enqueueToast(`Addendum ${addendumSMID} was not signed, an error occurred.`, {
                icon: 'redWarning',
              });
              logger.error('[Fields.Context] Error occurred when submitting addendum: ', e);
              options?.onError && options?.onError();
            },
          });

          if (!submitAddendumData?.submitAddendum) {
            setStatus(ReportStatus.AddendumAdd);
            options?.onError && options?.onError();
            return;
          }

          if (isBypassAndSign) {
            setStatus(ReportStatus.Submitted);
            // immediately show timestamp and user
            dispatch({
              type: 'finalizeProvisionalAddendum',
              payload: {
                submittingUserFirstName: me?.firstName,
                submittingUserLastName: me?.lastName,
                addendumSubmittedDate: submitAddendumData?.submitAddendum?.submittedAt,
              },
            });
          } else {
            startCountdown();
            setStatus(ReportStatus.ProvisionalAddendumSubmit);
          }

          dispatch({
            type: 'submitAddendum',
            payload: { addendumSubmittedDate: submitAddendumData?.submitAddendum?.submittedAt },
          });

          const defaultToastMsg = `Report for ${getCaseInfo(worklistItem)} has been signed.`;

          await maybeAutoloadNextCaseCallback(isWorklistAutoLoadEnabled, defaultToastMsg, {
            reportExitAction: isBypassAndSign ? ReportExitAction.BypassSign : ReportExitAction.Sign,
            defaultToastMsg,
            isAddendum: true,
          });
          setRightDrawerCatalogRefreshSequence((previous) => previous + 1);

          // If the user was closing the window, we want to close it now that the report has been signed
          options?.onSuccess && options.onSuccess();

          return;
        case ReportStatus.AddendumSubmitting:
          return;
        case ReportStatus.ProvisionalAddendumSubmit:
          return;
        case ReportStatus.AddendumCancelling:
          return;
        default:
          return unreachableCaseError(status);
      }
    },
    [
      isReporterSubmissionEnabled,
      isPrelimSubmissionEnabled,
      savingState,
      hasHeadingError,
      placeholderFieldDialogState.open,
      isEmptyRequiredFieldsDialogOpenForCurrentCase,
      status,
      fieldsUpdateDebounced,
      addendumUpdateDebounced,
      worklistItem,
      enqueueToast,
      openEmptyRequiredFieldsDialog,
      setAllRequiredFieldsTouched,
      isRecording,
      addendum?.content,
      setStatus,
      addendumSMID,
      analyticsData,
      trackTypedCharacters,
      submitAddendum,
      isBypassAndSign,
      maybeAutoloadNextCaseCallback,
      isWorklistAutoLoadEnabled,
      content,
      hasReportContentChanged,
      setHasReportContentChanged,
      isBypassAndSignPrelim,
      measureReportExitActionEndWithMetadata,
      stopRecording,
      setPlaceholderFieldDialogState,
      setSavingState,
      fieldsUpdate,
      addendumUpdate,
      activateTemplateID,
      submitPrelimReport,
      submitReport,
      enqueueOrUpdateToast,
      latestToastKey,
      setPrelimReportWorklistSmid,
      setRightDrawerCatalogRefreshSequence,
      startCountdown,
      resolveSubmissionWorkstationID,
      workstationID,
      me?.firstName,
      me?.lastName,
    ]
  );

  const handleAddendumAdd = useCallback(async () => {
    const reportSmid = getReportSMID(worklistItem);
    const { data } = await createAddendum({
      variables: {
        reportSmid,
      },
      update: (proxy, { data }) => {
        if (data == null) return;

        proxy.writeQuery({
          query: GET_WORKLIST_ITEM,
          data: {
            worklistItem: {
              ...worklistItem,
              report: {
                ...worklistItem.report,
                addendums: [...(worklistItem?.report?.addendums ?? []), data.createAddendum],
              },
            },
          },
        });
      },
      onError: (e) => {
        logger.error('[Fields.Context] Error occurred when creating addendum: ', e);

        enqueueToast('An error occurred when creating an addendum.', {
          icon: 'redWarning',
        });

        return;
      },
    });

    if (data == null) return;

    try {
      if (refetchCurrentCase) {
        await refetchCurrentCase();
      }
    } catch (error) {
      logger.warn('[Fields.Context] Failed to refetch case after creating addendum', {
        addendumSmid: data.createAddendum.smid,
        error,
        reportSmid,
        worklistItemSmid: worklistItem.smid,
      });
    }

    dispatch({ type: 'addAddendum' });
    setStatus(ReportStatus.AddendumAdd);
    resetEditorWithContent(getDefaultSlateFieldChildren());

    if (typeof data?.createAddendum?.smid === 'string') {
      dispatch({ type: 'addAddendumSMID', payload: { addendumSMID: data.createAddendum.smid } });
    }

    sendCrossTabEvent(NameSpaces.CROSS_WINDOW_DATA_REFETCH, {
      type: CrossWindowDataRefetchType.CLAIMED_ITEMS,
    });
  }, [
    createAddendum,
    worklistItem,
    setStatus,
    refetchCurrentCase,
    enqueueToast,
    resetEditorWithContent,
  ]);

  const handleCancelAddAddendum = useCallback(
    async ({
      onSuccess,
      onError,
    }: {
      onSuccess?: () => void;
      onError?: () => void;
    } = {}) => {
      if (addendumSMID == null) return;

      addendumUpdateDebounced.cancel();

      dispatch({ type: 'clearAddAddendum' });
      setStatus(ReportStatus.Submitted);
      await deleteAddendum({
        variables: { smid: addendumSMID },
        update: (proxy, { data }) => {
          if (data == null || !data.deleteAddendum.confirmed) return;

          proxy.writeQuery({
            query: GET_WORKLIST_ITEM,
            data: {
              worklistItem: {
                ...worklistItem,
                report: {
                  ...worklistItem.report,
                  addendums:
                    worklistItem?.report?.addendums?.filter(({ smid }) => smid !== addendumSMID) ??
                    [],
                },
              },
            },
          });
        },
        onCompleted: async () => {
          const defaultToastMsg = `Addendum for ${getCaseInfo(worklistItem)} has been discarded.`;

          await maybeAutoloadNextCaseCallback(isWorklistAutoLoadEnabled, defaultToastMsg, {
            reportExitAction: ReportExitAction.Discard,
            defaultToastMsg,
            isAddendum: true,
          });
          onSuccess && onSuccess();
        },
        onError: () => {
          onError && onError();
        },
      });
    },
    [
      addendumSMID,
      addendumUpdateDebounced,
      setStatus,
      deleteAddendum,
      worklistItem,
      maybeAutoloadNextCaseCallback,
      isWorklistAutoLoadEnabled,
    ]
  );

  useEffect(() => {
    if (templateInsertRequest == null) return;
    const { requestId } = templateInsertRequest;

    if (handledTemplateInsertRequestIdRef.current === requestId) {
      clearTemplateInsertRequestIfMatches(requestId);
      return;
    }

    logger.info('[Fields.Context] Processing template insertion request: ', {
      requestId: templateInsertRequest.requestId,
      requestedTemplateId: templateInsertRequest.templateId,
      requestSource: templateInsertRequest.source,
      requestedWorklistItemSmid: templateInsertRequest.worklistItemSmid,
      currentWorklistItemSmid: worklistItem.smid,
      autoloadTemplateState,
      status,
      isDisabled,
    });

    if (templateInsertRequest.worklistItemSmid !== worklistItem.smid) {
      handledTemplateInsertRequestIdRef.current = requestId;
      clearTemplateInsertRequestIfMatches(requestId);
      return;
    }
    if (isDisabled) return;
    if (template == null) return;
    if (template.id !== templateInsertRequest.templateId) return;

    const templateForbiddenStatuses: ReportStatuses[] = [
      ...IN_FLIGHT_STATUSES,
      ...IN_FLIGHT_PRELIM_STATUSES,
      ...PROVISIONAL_SUBMIT_STATUSES,
    ];
    if (templateForbiddenStatuses.includes(status)) return;

    // Wait for autoload decision before applying any template.
    // This prevents stale template state (e.g. from a previous case) from being inserted
    // while autoload resolution is still in progress for the current case.
    if (autoloadTemplateState === 'pending') return;

    // If autoloading was skipped (e.g. because the report already has saved content),
    // don't let the template overwrite the existing report.
    if (
      autoloadTemplateState === 'skipped' &&
      templateInsertRequest.source === InsertedTemplateDataSource.Autoload
    ) {
      handledTemplateInsertRequestIdRef.current = requestId;
      clearTemplateInsertRequestIfMatches(requestId);
      return;
    }

    try {
      handleInsertTemplate();
    } catch (error) {
      logger.error(
        '[Fields.Context] Error occurred while inserting template from request',
        {
          requestId,
          templateId: templateInsertRequest.templateId,
          source: templateInsertRequest.source,
          worklistItemSmid: templateInsertRequest.worklistItemSmid,
        },
        error
      );
    } finally {
      handledTemplateInsertRequestIdRef.current = requestId;
      clearTemplateInsertRequestIfMatches(requestId);
    }
  }, [
    templateInsertRequest,
    worklistItem.smid,
    template,
    autoloadTemplateState,
    isDisabled,
    status,
    handleInsertTemplate,
    clearTemplateInsertRequestIfMatches,
  ]);

  // This effect updates the client state to represent the `sent_at` status to mirth
  // (after 3 minutes, the report is locked in and cannot be edited further)
  useEffect(() => {
    if (countdownSeconds == null || countdownSeconds > 0) return;
    clearTimer();

    if (
      status === ReportStatus.ProvisionalSubmit ||
      status === ReportStatus.ProvisionalAddendumSubmit
    ) {
      setStatus(ReportStatus.Submitted);
    } else if (status === ReportStatus.ProvisionalPrelimSubmit) {
      setStatus(ReportStatus.Idle);
      setPrelimReportWorklistSmid(worklistItem.smid);
    }

    if (status === ReportStatus.ProvisionalAddendumSubmit) {
      dispatch({
        type: 'finalizeProvisionalAddendum',
        payload: { submittingUserFirstName: me?.firstName, submittingUserLastName: me?.lastName },
      });
    }
  }, [
    countdownSeconds,
    status,
    setStatus,
    clearTimer,
    me,
    worklistItem,
    setPrelimReportWorklistSmid,
  ]);

  useEffect(() => {
    if (isFocusModeOn && !isFocusModeAllowed) {
      toggleFocusMode({ method: FocusModeToggleMethod.FormControl, enabled: false });
    }
  }, [isDisabled, isFocusModeOn, isFocusModeAllowed, toggleFocusMode]);

  useEffect(() => {
    if (
      isDisabled ||
      status !== ReportStatus.Idle ||
      content === previousContent ||
      (updateType !== 'contentChange' && updateType !== 'templateApply')
    ) {
      return;
    }

    // saving state is set to ReporterSaveState.Saving outside of debounced callback, so that state is set as soon as the user makes a change to the fields
    setSavingState(ReporterSaveState.Saving);

    fieldsUpdateDebounced({
      contentToSubmit: content,
      activateTemplateIDToSubmit: activateTemplateID,
    });

    if (updateType === 'templateApply') {
      fieldsUpdateDebounced.flush();
    }
  }, [
    fieldsUpdateDebounced,
    content,
    previousContent,
    activateTemplateID,
    status,
    isDisabled,
    updateType,
    setSavingState,
    setHasReportContentChanged,
  ]);

  useEffect(() => {
    if (status === ReportStatus.Submitted && countdownSeconds === null) {
      worklistUpdateBc.postMessage({
        smid: worklistItem.smid,
        reason: WorklistRefetchReason.Countdown,
      });
    }
  }, [status, worklistItem.smid, countdownSeconds]);

  useEffect(() => {
    if (
      status !== ReportStatus.AddendumAdd ||
      previousAddendum?.content == null ||
      addendum?.content === previousAddendum?.content ||
      addendumSMID == null
    ) {
      return;
    }

    // saving state is set to ReporterSaveState.Saving outside of debounced callback, so that state is set as soon as the user makes a change to the addendum
    setSavingState(ReporterSaveState.Saving);

    addendumUpdateDebounced({ contentToSubmit: addendum?.content, addendumSMID });
  }, [addendumUpdateDebounced, addendum, previousAddendum, addendumSMID, status, setSavingState]);

  useCrossTabEventsListener(NameSpaces.CANCEL_REPORT_COUNTDOWN, ({ payload }) => {
    if (payload.worklistItemSmid === worklistItem.smid) {
      cancelCountdown();

      logger.info('[Fields.Context] Cancelling report timeline due to cross window event', {
        smid: worklistItem.smid,
        status: status,
      });

      if (status === ReportStatus.ProvisionalSubmit) {
        setRightDrawerCatalogRefreshSequence((previous) => previous + 1);
        setStatus(ReportStatus.Idle);
        analytics.track(reporter.usr.submitReportCancel, {
          ...analyticsData,
          smid: worklistItem?.report?.smid,
        });
      } else if (status === ReportStatus.ProvisionalAddendumSubmit) {
        setRightDrawerCatalogRefreshSequence((previous) => previous + 1);
        setStatus(ReportStatus.AddendumAdd);
        analytics.track(reporter.usr.submitReportCancel, {
          ...analyticsData,
          smid: addendumSMID,
        });
      }
    }
  });

  // The user can submit the report with a voice command, keyboard shortcut, or gamepad button
  // voice command:
  useRecorderVoiceCommandEventsListener(({ payload }) => {
    if (isLinkCasesMutating && isLinkCasesBlockedVoiceAction(payload.substitution.action)) {
      return;
    }

    if (payload.substitution.action === 'SubmitReport') {
      measureReportExitActionStartWithMetadata({
        exitAction: ReportExitAction.Sign,
        inputMethod: ReportExitActionInputMethod.VoiceCommand,
        hasConfirmationDialogTriggered: false,
      });
      if (unsignedReportDialogOptions.open) {
        handleSubmit({
          onSuccess: onDialogActionSuccess,
          onError: onDialogActionError,
        });
      } else {
        handleSubmit();
      }
    }

    if (payload.substitution.action === 'DraftReport') {
      handleDraftAction();
    }

    if (payload.substitution.action === 'DiscardReport') {
      if (status === ReportStatus.AddendumAdd) {
        handleCancelAddAddendum();
      } else if (discardReportOptions.open === true || unsignedReportDialogOptions.open === true) {
        // Second press while the dialog is open intentionally maps to Discard (destructive) —
        // preserves the pre-tri-state muscle memory. Keep Draft is mouse-only.
        handleDiscardReport({
          onSuccess: onDialogActionSuccess,
          onError: onDialogActionError,
        });
      } else {
        handleOpenDiscardReportModal();
      }
    }
  });

  // keyboard shortcut:
  useCrossTabEventsListener(NameSpaces.REPORTER_SHORTCUT, ({ payload }) => {
    if (isLinkCasesMutating && isLinkCasesBlockedShortcutAction(payload.action)) {
      return;
    }

    if (payload.action === 'submitReport') {
      measureReportExitActionStartWithMetadata({
        exitAction: ReportExitAction.Sign,
        inputMethod: ReportExitActionInputMethod.Keyboard,
        hasConfirmationDialogTriggered: false,
      });
      if (unsignedReportDialogOptions.open) {
        handleSubmit({
          onSuccess: onDialogActionSuccess,
          onError: onDialogActionError,
        });
      } else {
        handleSubmit();
      }
    }

    if (payload.action === 'draftReport') {
      handleDraftAction();
    }

    if (payload.action === 'discardReport') {
      if (status === ReportStatus.AddendumAdd) {
        handleCancelAddAddendum();
      } else if (discardReportOptions.open === true || unsignedReportDialogOptions.open === true) {
        // Second press while the dialog is open intentionally maps to Discard (destructive) —
        // preserves the pre-tri-state muscle memory. Keep Draft is mouse-only.
        handleDiscardReport({
          onSuccess: onDialogActionSuccess,
          onError: onDialogActionError,
        });
      } else {
        handleOpenDiscardReportModal();
      }
    }
  });

  // custom event:
  useEffect(() => {
    const handler = (event: CustomEvent<string>) => {
      const { detail } = event;
      if (isLinkCasesMutating && isLinkCasesBlockedExtensionShortcutDetail(detail)) {
        return;
      }

      if (
        detail === 'reporter-submit-report' &&
        status !== ReportStatus.ProvisionalSubmit &&
        status !== ReportStatus.ProvisionalAddendumSubmit
      ) {
        measureReportExitActionStartWithMetadata({
          exitAction: ReportExitAction.Sign,
          inputMethod: ReportExitActionInputMethod.Keyboard,
          hasConfirmationDialogTriggered: false,
        });
        handleSubmit();
      }

      if (detail === 'reporter-discard-report') {
        if (status === ReportStatus.AddendumAdd) {
          handleCancelAddAddendum();
        } else if (
          discardReportOptions.open === true ||
          unsignedReportDialogOptions.open === true
        ) {
          // Second press while the dialog is open intentionally maps to Discard (destructive) —
          // preserves the pre-tri-state muscle memory. Keep Draft is mouse-only.
          handleDiscardReport({
            onSuccess: onDialogActionSuccess,
            onError: onDialogActionError,
          });
        } else {
          handleOpenDiscardReportModal();
        }
      }
    };

    addExtensionListener(
      EXTENSION_RESPONSE_EVENTS.SHORTCUT_TRIGGERED,
      handler as unknown as EventListener
    );
    return () =>
      removeExtensionListener(
        EXTENSION_RESPONSE_EVENTS.SHORTCUT_TRIGGERED,
        handler as unknown as EventListener
      );
  }, [
    discardReportOptions.open,
    handleCancelAddAddendum,
    handleDiscardReport,
    handleOpenDiscardReportModal,
    handleSubmit,
    onDialogActionError,
    onDialogActionSuccess,
    status,
    unsignedReportDialogOptions.open,
    measureReportExitActionStartWithMetadata,
    isLinkCasesMutating,
  ]);

  // gamepad button:
  const gamepadCallback = useCallback(
    (params: { action: GamepadActionId; pressed: boolean }) => {
      const { action, pressed } = params;
      if (
        isLinkCasesMutating &&
        pressed &&
        [
          GamepadActionId.SubmitReport,
          GamepadActionId.DraftReport,
          GamepadActionId.SubmitPrelimReport,
        ].includes(action)
      ) {
        return;
      }

      const isProvisionalSubmittingStatus =
        status === ReportStatus.ProvisionalSubmit ||
        status === ReportStatus.ProvisionalAddendumSubmit ||
        status === ReportStatus.ProvisionalPrelimSubmit;

      if (action === GamepadActionId.SubmitReport && pressed && !isProvisionalSubmittingStatus) {
        measureReportExitActionStartWithMetadata({
          exitAction: ReportExitAction.Sign,
          inputMethod: ReportExitActionInputMethod.ControlInputDevice,
          hasConfirmationDialogTriggered: false,
        });

        handleSubmit();
        return;
      }

      if (action === GamepadActionId.DraftReport && pressed) {
        handleSaveDraft();
        return;
      }

      if (action === GamepadActionId.GenerateImpression && pressed && isAutoImpressionsEnabled) {
        sendCrossTabEvent(NameSpaces.REPORTER_SHORTCUT, {
          type: 'gamepad',
          action: 'generateImpression',
          pressed: true,
        });
        return;
      }

      if (
        action === GamepadActionId.SubmitPrelimReport &&
        pressed &&
        isPrelimSubmissionEnabled &&
        !isProvisionalSubmittingStatus
      ) {
        measureReportExitActionStartWithMetadata({
          exitAction: ReportExitAction.Prelim,
          inputMethod: ReportExitActionInputMethod.ControlInputDevice,
          hasConfirmationDialogTriggered: false,
        });

        handleSubmit({ type: SubmitType.Prelim });
        return;
      }

      if (action === GamepadActionId.ToggleVisibility && pressed) {
        dispatchExtensionEvent(focusEvent());
        return;
      }

      if (action === GamepadActionId.ToggleFocusMode && pressed && isFocusModeAllowed) {
        toggleFocusMode({ method: FocusModeToggleMethod.Dictaphone });
      }
    },
    [
      status,
      handleSubmit,
      handleSaveDraft,
      toggleFocusMode,
      isFocusModeAllowed,
      isAutoImpressionsEnabled,
      isPrelimSubmissionEnabled,
      isLinkCasesMutating,
      measureReportExitActionStartWithMetadata,
    ]
  );

  useGamepadBindings({ callback: gamepadCallback, context: APOLLO_QUERY_CONTEXT });
  useWebHIDBindings({ callback: gamepadCallback });

  const dispatchBag = useMemo(
    () => ({
      dispatch,
      handleInsertTemplate,
      onReportChange,
      handleSubmit,
      handleAddendumAdd,
      handleCancelSubmit,
      handleCancelAddAddendum,
      onAddendumChange,
      handleSaveDraft,
    }),
    [
      dispatch,
      handleInsertTemplate,
      onReportChange,
      handleSubmit,
      handleCancelSubmit,
      handleAddendumAdd,
      handleCancelAddAddendum,
      onAddendumChange,
      handleSaveDraft,
    ]
  );

  const countdownBag = useMemo(() => ({ countdownSeconds }), [countdownSeconds]);
  return (
    <FieldsStateContext.Provider value={state}>
      <FieldsDispatchContext.Provider value={dispatchBag}>
        <FieldsCountdownContext.Provider value={countdownBag}>
          {children}
          <WorkstationIDSubmissionDialog {...workstationPromptDialogProps} />
        </FieldsCountdownContext.Provider>
      </FieldsDispatchContext.Provider>
    </FieldsStateContext.Provider>
  );
};

FieldsProvider.displayName = 'FieldsProvider';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(FieldsProvider);

export const useFieldsState = (): FieldsState => {
  const fieldsContext = useContext(FieldsStateContext);

  if (!fieldsContext) {
    throw new Error('useFieldsState must be used within a FieldsProvider.');
  }

  return fieldsContext;
};

export const useFieldsCountdown = (): FieldsCountdownContextProps => {
  const fieldsContext = useContext(FieldsCountdownContext);

  if (!fieldsContext) {
    throw new Error('useFieldsCountdown must be used within a FieldsProvider.');
  }

  return fieldsContext;
};

export const useFieldsDispatch = (): FieldsDispatch => {
  const fieldsDispatch = useContext(FieldsDispatchContext);

  if (!fieldsDispatch) {
    throw new Error('useFieldsDispatch must be used within a FieldsProvider.');
  }

  return fieldsDispatch;
};

export const useFields = (): [FieldsState, FieldsDispatch] => {
  return [useFieldsState(), useFieldsDispatch()];
};
